import React from 'react'
import classnames from 'classnames'
import LanguageIcon from '@material-ui/icons/Language'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core'

export type TranslationChipStatus = 'translated' | 'generating' | 'untranslated' | 'partial'

const useStyles = makeStyles<Theme, { status: TranslationChipStatus }>((theme: Theme) => ({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    borderRadius: 999,
    padding: theme.spacing(0.25, 1),
    // 'untranslated' and 'partial' use a warning treatment so an incomplete translation is
    // obvious at a glance rather than looking indistinguishable from a real success.
    backgroundColor: ({ status }) =>
      status === 'untranslated' || status === 'partial'
        ? theme.palette.warning.light
        : theme.paintscout.palette.secondary[230].C95,
    color: ({ status }) =>
      status === 'untranslated' || status === 'partial' ? theme.palette.warning.dark : theme.palette.primary.main
  },
  icon: {
    fontSize: '1rem'
  },
  label: {
    ...theme.typography.subtitle2,
    lineHeight: 1
  }
}))

export interface TranslationStatusChipProps {
  /**
   * 'generating' while a translation is being produced, 'translated' once fully complete,
   * 'partial' when some but not all content translated (a real failure, not just "in progress"
   * -- see #21434), and 'untranslated' when the language hasn't been generated yet.
   */
  status: TranslationChipStatus
  /**
   * Human-readable language name (e.g. "Spanish"). When provided it's woven into the label —
   * "Generating Spanish…" / "Spanish Generated" / "Spanish Partially Generated" /
   * "Spanish Not Generated". Falls back to generic "Generating…" / "Translated" /
   * "Partially Translated" / "Not Generated".
   */
  language?: string
  className?: string
}

/**
 * Small pill shown next to the work-order title to reflect a language's translation state:
 * "Generating…" while being produced, "Generated" once fully ready, "Partially Generated"
 * (warning styling) when some content failed to translate, and "Not Generated" (warning styling)
 * when the selected language still needs to be generated.
 */
export default function TranslationStatusChip({ status, language, className }: TranslationStatusChipProps) {
  const classes = useStyles({ status })

  let label: string
  if (status === 'generating') {
    label = language ? `Generating ${language}…` : 'Generating…'
  } else if (status === 'untranslated') {
    label = language ? `${language} Not Generated` : 'Not Generated'
  } else if (status === 'partial') {
    label = language ? `${language} Partially Generated` : 'Partially Translated'
  } else {
    label = language ? `${language} Generated` : 'Translated'
  }

  const Icon = status === 'untranslated' || status === 'partial' ? ErrorOutlineIcon : LanguageIcon

  return (
    <span className={classnames(classes.root, className)}>
      <Icon className={classes.icon} />
      <span className={classes.label}>{label}</span>
    </span>
  )
}
